import { c as _c } from "react/compiler-runtime";
import type { PlayInfo } from '@module/apis/client-side/live/type';
import ChatTranslateButton from '@module/components/Translate/ChatTranslateButton';
import NickTranslateButton from '@module/components/Translate/NickTranslateButton';
import SettingConfig from '@module/config/setting';
import { nickDecoTextColor } from '@module/constants/item';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { cn } from '@module/lib/utils';
import useModalStore from '@module/stores/useModalStore';
import type { NormalChatMessage, NormalChatMessageType } from '@module/types/centrifuge/public-message/normalChatMessageTypes';
import type { FanLevel } from '@module/types/centrifuge/publicMessageTypes';
import { getChatUserFlags, getTranslateFlags } from '@module/utils/chatUserType';
import { truncateNormalChatMessage } from '@module/utils/truncateText';
import ToastBody from '@pc/components/atoms/toast/ToastBody';
import VipDecoBadge from '@pc/components/badge/VipDecoBadge';
import { type Dispatch, type SetStateAction, useState } from 'react';
import toast, { type Toast } from 'react-hot-toast';
import ChatGradePrefixImage from '../../../_components/ChatGradePrefixImage';
import ViewerInteractionMenu from '../../../_components/ViewerInteractionMenu';
import ChatGenderPrefixImage from '../ChatGenderPrefixImage';
interface Props {
  item: NormalChatMessage;
  playInfo: PlayInfo;
  isRoomMaster: boolean;
  isManager: boolean;
  menuVisibleOffset?: number;
  setMenuVisibleOffset?: Dispatch<SetStateAction<number>>;
}

/**
 * @param level - 사용자 레벨 (0-5, optional)
 * @param type - 사용자 타입 ('bj', 'chatter', 'manager', 'guest', 'support')
 * @param hasNickDeco - 닉네임 장식 아이템 보유 여부
 */
function getNicknameTextColorClasses(level: FanLevel | undefined, type: NormalChatMessageType, hasNickDeco: boolean) {
  return {
    'text-[#e3ba5e]': !hasNickDeco && level === 1,
    'text-[#29deff]': !hasNickDeco && level === 2,
    'text-[#ff8a00]': !hasNickDeco && level === 3,
    'text-[#e040fb]': !hasNickDeco && level === 4,
    'text-[#f50090]': !hasNickDeco && level === 5,
    'text-[#ffa726]': type === 'bj',
    'text-system-dark-blue': type === 'support'
  };
}
export default function NormalChat(props) {
  const $ = _c(100);
  const {
    item,
    playInfo,
    isRoomMaster,
    isManager,
    menuVisibleOffset,
    setMenuVisibleOffset
  } = props;
  const {
    isLogin
  } = useGetLoginInfoQuery();
  const pushModal = useModalStore(_temp);
  const [translatedMessage, setTranslatedMessage] = useState(null);
  const [translatedNickname, setTranslatedNickname] = useState(null);
  const ndc = item.data?.ndc;
  const isNickDecoWithIcon = typeof ndc === "number" && ndc % 2 === 1;
  let t0;
  if ($[0] !== item) {
    t0 = truncateNormalChatMessage(item);
    $[0] = item;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const message = t0;
  let t1;
  if ($[2] !== item.data.type) {
    t1 = getChatUserFlags(item.data.type);
    $[2] = item.data.type;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    userIsSupport,
    userIsManager,
    userIsRoomMaster
  } = t1;
  const t2 = !!item.data.nk;
  const t3 = message.length > 0;
  let t4;
  if ($[4] !== isRoomMaster || $[5] !== item.data.lang || $[6] !== item.data.type || $[7] !== t2 || $[8] !== t3) {
    t4 = getTranslateFlags({
      userType: item.data.type,
      lang: item.data.lang,
      hasNickname: t2,
      hasMessage: t3,
      viewerIsRoomMaster: isRoomMaster
    });
    $[4] = isRoomMaster;
    $[5] = item.data.lang;
    $[6] = item.data.type;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    isJapaneseViewer,
    isNickTranslateEnabled,
    isMessageTranslateEnabled
  } = t4;
  let t10;
  let t11;
  let t12;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== isLogin || $[11] !== isNickDecoWithIcon || $[12] !== isNickTranslateEnabled || $[13] !== isRoomMaster || $[14] !== item.data.emoticon || $[15] !== item.data.lev || $[16] !== item.data.nk || $[17] !== item.data.rk || $[18] !== item.data.sex || $[19] !== item.data.type || $[20] !== item.data.vdc || $[21] !== item.offset || $[22] !== message || $[23] !== ndc || $[24] !== setMenuVisibleOffset || $[25] !== translatedNickname || $[26] !== userIsSupport) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const nicknameTextColorClasses = getNicknameTextColorClasses(item.data.lev, item.data.type, !!ndc);
      if (!isRoomMaster && userIsSupport) {
        t12 = null;
        break bb0;
      }
      t9 = "relative mb-1.5 flex flex-col whitespace-pre-wrap break-all";
      let t13;
      if ($[35] !== ndc) {
        t13 = ndc ? {
          backgroundImage: `url('${SettingConfig.S3_BUCKET_URL}/img/item/nickDeco/${Math.floor((ndc + 1) / 2)}_bg.png')`
        } : {};
        $[35] = ndc;
        $[36] = t13;
      } else {
        t13 = $[36];
      }
      const t14 = `relative flex h-[22.5px] items-center justify-start text-baseFg [&>*]:inline ${ndc ? `text-shadow-deco-${Math.floor((ndc + 1) / 2)}` : ""}`;
      const t15 = ndc === 91 || ndc === 92;
      const t16 = ndc === 85 || ndc === 86 || ndc === 89 || ndc === 90 || ndc === 93 || ndc === 94;
      let t17;
      if ($[37] !== ndc || $[38] !== t14 || $[39] !== t15 || $[40] !== t16) {
        t17 = cn(t14, {
          "mb-0.5 rounded-full bg-[100%_0] bg-[size:auto_100%] bg-repeat-x pr-0.5 pl-1": ndc,
          "border border-[#ff8f3d]": t15,
          "rounded-none": t16
        });
        $[37] = ndc;
        $[38] = t14;
        $[39] = t15;
        $[40] = t16;
        $[41] = t17;
      } else {
        t17 = $[41];
      }
      let t18;
      if ($[42] !== isLogin || $[43] !== item.offset || $[44] !== setMenuVisibleOffset) {
        t18 = () => {
          if (!isLogin || !setMenuVisibleOffset || !item.offset) {
            return;
          }
          setMenuVisibleOffset(item.offset);
        };
        $[42] = isLogin;
        $[43] = item.offset;
        $[44] = setMenuVisibleOffset;
        $[45] = t18;
      } else {
        t18 = $[45];
      }
      let t19;
      if ($[46] !== item.data.vdc) {
        t19 = !!item?.data?.vdc && <div className="mr-1 grid place-items-center"><VipDecoBadge vipDecoCode={item.data.vdc} /></div>;
        $[46] = item.data.vdc;
        $[47] = t19;
      } else {
        t19 = $[47];
      }
      const t20 = item.data?.lev;
      const t21 = item.data?.rk;
      let t22;
      if ($[48] !== item.data.type || $[49] !== t20 || $[50] !== t21) {
        t22 = <ChatGradePrefixImage type={item.data.type} level={t20} ranking={t21} className="mr-1 h-[15px] w-[15px]" />;
        $[48] = item.data.type;
        $[49] = t20;
        $[50] = t21;
        $[51] = t22;
      } else {
        t22 = $[51];
      }
      let t23;
      if ($[52] !== item.data.sex || $[53] !== item.data.type) {
        t23 = item.data.type !== "support" && item.data.sex && <ChatGenderPrefixImage type={item.data.sex} className="mr-1" />;
        $[52] = item.data.sex;
        $[53] = item.data.type;
        $[54] = t23;
      } else {
        t23 = $[54];
      }
      let t24;
      if ($[55] !== item.data.type || $[56] !== ndc) {
        t24 = ndc && !["bj"].includes(item.data.type) ? {
          color: nickDecoTextColor[Math.floor((ndc + 1) / 2)]
        } : {};
        $[55] = item.data.type;
        $[56] = ndc;
        $[57] = t24;
      } else {
        t24 = $[57];
      }
      let t25;
      if ($[58] !== isNickDecoWithIcon || $[59] !== ndc) {
        t25 = isNickDecoWithIcon && <img src={`${SettingConfig.S3_BUCKET_URL}/img/item/nickDeco/${(ndc + 1) / 2}_icon.png`} alt="icon" className={cn("absolute top-0 right-0 h-[22.5px] translate-x-[calc(100%-9px)]", {
          "-top-[1px]": ndc === 91
        })} />;
        $[58] = isNickDecoWithIcon;
        $[59] = ndc;
        $[60] = t25;
      } else {
        t25 = $[60];
      }
      const t26 = <div style={t13} className={t17} onClick={t18}>{t19}{t22}{t23}<span style={t24} className={cn("relative text-nowrap", nicknameTextColorClasses)}>{userIsSupport ? "\uC11C\uD3EC\uD130" : item.data.nk}</span>{t25}<span className={cn("relative ml-1 text-[13px]", nicknameTextColorClasses, {
          "-right-2": !!ndc,
          "-right-8": isNickDecoWithIcon
        })}>:</span></div>;
      if ($[61] !== t26) {
        t10 = <div className="flex items-center text-cht_baseFg">{t26}</div>;
        $[61] = t26;
        $[62] = t10;
      } else {
        t10 = $[62];
      }
      if ($[63] !== item.data.emoticon) {
        t11 = item.data.emoticon?.block && <div className="my-[3px] h-[72px] w-[72px] overflow-x-hidden text-left text-cht_baseFg"><img src={item.data.emoticon.block.format === "gif" ? item.data.emoticon.block.anim : item.data.emoticon.block.img} alt="emoticon" /></div>;
        $[63] = item.data.emoticon;
        $[64] = t11;
      } else {
        t11 = $[64];
      }
      t5 = "normal-chat-message";
      t6 = "w-full overflow-hidden text-left text-cht_baseFg";
      t7 = message;
      t8 = isNickTranslateEnabled && <><div className="flex items-center pt-[5px]"><NickTranslateButton nickname={item.data.nk || ""} setTranslatedNickname={setTranslatedNickname} srcLang="ja" />{translatedNickname && <span className={cn("relative whitespace-nowrap pl-[4px] text-[13px]", nicknameTextColorClasses)}>{translatedNickname}</span>}</div></>;
    }
    $[10] = isLogin;
    $[11] = isNickDecoWithIcon;
    $[12] = isNickTranslateEnabled;
    $[13] = isRoomMaster;
    $[14] = item.data.emoticon;
    $[15] = item.data.lev;
    $[16] = item.data.nk;
    $[17] = item.data.rk;
    $[18] = item.data.sex;
    $[19] = item.data.type;
    $[20] = item.data.vdc;
    $[21] = item.offset;
    $[22] = message;
    $[23] = ndc;
    $[24] = setMenuVisibleOffset;
    $[25] = translatedNickname;
    $[26] = userIsSupport;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t10 = $[27];
    t11 = $[28];
    t12 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[65] !== isMessageTranslateEnabled || $[66] !== isRoomMaster || $[67] !== item || $[68] !== pushModal) {
    t13 = isMessageTranslateEnabled && <div className="pt-[2px]"><ChatTranslateButton className="m-0 px-0 text-colorFg02 disabled:text-colorFg16" chatData={item} setTranslatedMessage={setTranslatedMessage} filtered={item.data?.filtered} filteredMessage={"\uAE08\uCE59\uC5B4\uAC00 \uD3EC\uD568\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4."} onError={_temp2} onClickWithoutLogin={() => {
        pushModal({
          type: "AUTH_MODAL"
        });
      }} isRoomMaster={isRoomMaster} /></div>;
    $[65] = isMessageTranslateEnabled;
    $[66] = isRoomMaster;
    $[67] = item;
    $[68] = pushModal;
    $[69] = t13;
  } else {
    t13 = $[69];
  }
  let t14;
  if ($[70] !== isMessageTranslateEnabled || $[71] !== translatedMessage) {
    t14 = isMessageTranslateEnabled && translatedMessage && <div className="flex gap-1.5 text-cht_baseFg">ㄴ<p>{translatedMessage}</p></div>;
    $[70] = isMessageTranslateEnabled;
    $[71] = translatedMessage;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== t13 || $[74] !== t14 || $[75] !== t5 || $[76] !== t6 || $[77] !== t7 || $[78] !== t8) {
    t15 = <div id={t5} className={t6}>{t7}{t8}{t13}{t14}</div>;
    $[73] = t13;
    $[74] = t14;
    $[75] = t5;
    $[76] = t6;
    $[77] = t7;
    $[78] = t8;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  let t16;
  if ($[80] !== isJapaneseViewer || $[81] !== isManager || $[82] !== isRoomMaster || $[83] !== item.data.id || $[84] !== item.data.idx || $[85] !== item.data.nk || $[86] !== item.offset || $[87] !== menuVisibleOffset || $[88] !== playInfo || $[89] !== setMenuVisibleOffset || $[90] !== userIsManager || $[91] !== userIsRoomMaster || $[92] !== userIsSupport) {
    t16 = item.data.id && item.data.nk && item.data.idx && <ViewerInteractionMenu playInfo={playInfo} isVisible={!!menuVisibleOffset && item.offset === menuVisibleOffset} user={{
      isManager,
      isRoomMaster
    }} viewer={{
      idx: item.data.idx,
      id: item.data.id,
      nick: item.data.nk,
      isJapanese: !!isJapaneseViewer,
      isManager: userIsManager,
      isRoomMaster: userIsRoomMaster,
      isSupport: userIsSupport
    }} onClose={() => setMenuVisibleOffset?.(0)} />;
    $[80] = isJapaneseViewer;
    $[81] = isManager;
    $[82] = isRoomMaster;
    $[83] = item.data.id;
    $[84] = item.data.idx;
    $[85] = item.data.nk;
    $[86] = item.offset;
    $[87] = menuVisibleOffset;
    $[88] = playInfo;
    $[89] = setMenuVisibleOffset;
    $[90] = userIsManager;
    $[91] = userIsRoomMaster;
    $[92] = userIsSupport;
    $[93] = t16;
  } else {
    t16 = $[93];
  }
  let t17;
  if ($[94] !== t10 || $[95] !== t11 || $[96] !== t15 || $[97] !== t16 || $[98] !== t9) {
    t17 = <div className={t9}>{t10}{t11}{t15}{t16}</div>;
    $[94] = t10;
    $[95] = t11;
    $[96] = t15;
    $[97] = t16;
    $[98] = t9;
    $[99] = t17;
  } else {
    t17 = $[99];
  }
  return t17;
}
function _temp2(error) {
  toast(m => <ToastBody id={m.id} content={error.message} />);
}
function _temp(state) {
  return state.pushModal;
}